import { currencyFloatToInteger, integerToCurrencyFloat } from '@acme/b2b/pricing';
import { ACMETenantResponse, TenantData, ThemesData } from '@b2b/common/resolvers';
import { ApiService } from '@b2b/common/services/api/types';
import { ThemesHelperService } from '@b2b/common/services/themesHelper';
import { ValidateService } from '@b2b/common/services/validate';
import { ensureArray } from '@b2b/common/utils/coerce-array';
import { ACMEResponse, TODO } from '@b2b/types/core';
import { ACMEUserConfigResponse } from '@b2b/types/user';
import { StateService } from '@uirouter/angularjs';
import angular, {
  IDocumentService,
  IFilterService,
  IQService,
  ISCEService,
  IScope,
  ITimeoutService,
  IWindowService,
} from 'angular';
import { OrdersDetailServiceFactory, OrdersDetailServiceModule } from '../../detail/services/orders-detail.service';
import { partitionAddonsAndFeesLast } from '../../utils';

OrdersPrintController.$inject = [
  '$scope',
  '$state',
  '$document',
  '$window',
  '$q',
  '$sce',
  '$timeout',
  '$filter',
  'ApiService',
  'HelpersService',
  'ValidateService',
  'CalendarService',
  'OrderService',
  'UserPreferencesService',
  'AuthService',
  'ThemesHelperService',
  'OrdersDetailServiceFactory',
  'UserConfigData',
  'TenantData',
  'ThemesData',
];

function OrdersPrintController(
  $scope: IScope & { ordersPrint: TODO },
  $state: StateService,
  $document: IDocumentService,
  $window: IWindowService,
  $q: IQService,
  $sce: ISCEService,
  $timeout: ITimeoutService,
  $filter: IFilterService,
  ApiService: ApiService,
  HelpersService: TODO,
  ValidateService: ValidateService,
  CalendarService: TODO,
  OrderService: TODO,
  UserPreferencesService: TODO,
  AuthService: TODO,
  ThemesHelperService: ThemesHelperService,
  OrdersDetailServiceFactory: OrdersDetailServiceFactory,
  UserConfigData: { data: ACMEUserConfigResponse },
  TenantData: { data: ACMETenantResponse<TenantData> },
  ThemesData: { data: ACMEResponse<ThemesData> },
) {
  const ordersDetailService = OrdersDetailServiceFactory.createInstance({
    onOnlinePaymentLinkChanged: (params) => {
      ctrl.onlinePaymentLink = ctrl.generateOnlinePaymentLink(params.onlinePaymentUUID);
      $scope.$apply();
    },
  });

  const ctrl = {
    model: {} as TODO,

    tenant: {} as TenantData,
    events: {},
    themes: {},

    transactions: [],

    isAccount: false,
    printReady: false,
    isLoaded: false,
    hasData: false,
    showDiscounted: false,
    hasExtRefId: true,

    getItemType: OrderService.getItemType,

    currentDate: '',

    totalPaid: '',
    totalRetail: 0,
    orderBalance: 0,
    dateFormat: UserPreferencesService.getDateFormat('midDate', 'dateFormat'),
    hourFormat: UserPreferencesService.getDateFormat('standardTime', 'hourFormat'),
    totalTicketQuantity: 0,
    baseTotalTicketQuantity: 0,
    totalAddons: 0,
    baseTotalAddons: 0,
    totalMemberships: 0,
    baseTotalMemberships: 0,
    hideItems: false,

    order: {} as TODO,
    accountCompany: {} as TODO,
    primaryContact: {} as TODO,
    accountContact: [] as TODO[],
    checkNumber: '',
    voucherNumber: '',

    resizable: 'yes',
    tableRatio: 0,

    onlinePaymentLink: '',

    enableSalesTax: false,
    includesAtLeastOneItemWithBasePrice: false,
    feeItems: [] as TODO[],
    isTaxExempt: false,

    init: function () {
      $scope.$on('$destroy', () => {
        ordersDetailService.destroy();
      });

      if (AuthService.getCookie('user')) {
        // Main entry point of application should check if user has permissions
        // If a user is logged in but has no permissions we should log them out
        let userSettings = {};

        if (UserConfigData.data && UserConfigData.data.success) {
          userSettings = UserConfigData.data.user;
        }

        UserPreferencesService.process(null, userSettings);
        ctrl.onDateFormatLoad();
      } else {
        // User cookie has disappeared log user out
        $state.go('logout.default', {}, { notify: true });
      }

      if (TenantData.data && TenantData.data.success) {
        ctrl.getTenant(TenantData.data.tenant);
      }

      if (ThemesData && ThemesData.data.success) {
        ThemesHelperService.setModel(ThemesData.data.payload);

        ctrl.enableSalesTax = ThemesHelperService.getFeatureFlag('enableSalesTax');
      }

      ctrl.order = HelpersService.getCookie('printorder');

      if (ctrl.order && ctrl.order.orderId) {
        ctrl.getOrderEventDetails(ctrl.order.orderId);
        ctrl.getThemes();
        ctrl.getTransactions(ctrl.order.orderId);

        $scope.$on(
          '$destroy',
          $scope.$on('AppController::print', function (e, data) {
            if (ctrl.printReady) {
              ctrl.printPage();
            }
          }),
        );
      }

      $scope.$on('$destroy', $scope.$on('header::hideItems', ctrl.onHideItemsUpdate));
    },

    getOrderEventDetails: function (orderId) {
      ApiService.orders.events(orderId).then(ctrl.success.getOrderEventDetails, ctrl.error.getOrderEventDetails);
    },

    getTenant: function (tenantData: TenantData) {
      ctrl.tenant = tenantData;
    },

    getThemes: function () {
      ApiService.themes.detail(true).then(ctrl.success.getThemes, ctrl.error.getThemes);
    },

    getAccountContacts: function (id) {
      ApiService.partners.contacts
        .list(id, HelpersService.getPaginator())
        .then(ctrl.success.getAccountContacts, ctrl.error.getAccountContacts);
    },

    getPartner: function (accountId) {
      ApiService.partners.detail(accountId).then(ctrl.success.getPartner, ctrl.error.getPartner);
    },

    processOrderEvents: function (orderEvents) {
      for (let i = 0, ii = orderEvents.length; i < ii; i++) {
        if (!ctrl.events[orderEvents[i].id]) {
          ctrl.events[orderEvents[i].id] = orderEvents[i];
        }
      }
    },

    getTransactions: function (orderId) {
      ApiService.orders.transactions(orderId).then(ctrl.success.getTransactions, ctrl.error.getTransactions);
    },

    getOrder: function (orderId) {
      ApiService.orders.willCall.order(orderId).then(ctrl.success.getOrder, ctrl.error.getOrder);
    },

    getOrderBalance: function () {
      return currencyFloatToInteger(ctrl.model.orderBalance || 0);
    },

    sortOrderEventItems: function (a, b) {
      const aSubtotal = parseFloat(ctrl.isDiscounted(a) ? a.discountedSubtotal : a.subtotal);
      const bSubtotal = parseFloat(ctrl.isDiscounted(b) ? b.discountedSubtotal : b.subtotal);

      if (bSubtotal > aSubtotal) {
        return 1;
      }
      if (bSubtotal < aSubtotal) {
        return -1;
      }
      if (a.itemType.toLowerCase() === 'inventory') {
        return 1;
      }
      if (a.itemType.toLowerCase() === 'event') {
        return -1;
      }
      if (a.itemTypeName > b.itemTypeName) {
        return 1;
      }
      if (a.itemTypeName < b.itemTypeName) {
        return -1;
      }
      return 0;
    },

    isMembership: function (event) {
      return event.eventId.toLowerCase() === 'membership';
    },

    isAddon: function (event) {
      return event.eventId.toLowerCase() === 'addons';
    },

    isFee: function (event) {
      return event.eventId === 'Fees';
    },

    isEvent: function (event) {
      return event.eventId.toLowerCase() !== 'membership' && event.eventId.toLowerCase() !== 'addons';
    },

    hasItemsToDisplay: function (event) {
      return (event.items || []).some((item) => item.quantity > 0);
    },

    getOrderMembershipLevelName: function (membershipItem) {
      if (membershipItem.membershipLevelName) {
        return membershipItem.membershipLevelName + ': ' + (membershipItem.itemTypeName || '');
      } else {
        return membershipItem.itemTypeName || '';
      }
    },

    process: function (order) {
      // Store fee items for totals, then merge into addons for display
      const feeEvents = order.eventItems.filter((e) => ctrl.isFee(e));
      ctrl.feeItems = feeEvents.flatMap((e) => e.items);
      if (feeEvents.length) {
        let addonsEvent = order.eventItems.find((e) => ctrl.isAddon(e));
        if (!addonsEvent) {
          addonsEvent = { eventId: 'addons', items: [] };
          order.eventItems.push(addonsEvent);
        }
        for (const feeEvent of feeEvents) {
          addonsEvent.items.push(...feeEvent.items);
        }
        order.eventItems = order.eventItems.filter((e) => !ctrl.isFee(e));
      }

      order.eventItems = partitionAddonsAndFeesLast(order.eventItems);

      for (let i = 0, ii = order.eventItems.length; i < ii; i++) {
        if (order.eventItems[i].eventDate) {
          order.eventItems[i].startDate = $filter('date')(
            CalendarService.parseDate(order.eventItems[i].eventDate),
            UserPreferencesService.getDateFormat('midDate', 'dateFormat'),
          );
          order.eventItems[i].startHour = $filter('date')(
            CalendarService.parseDate(order.eventItems[i].eventDate),
            UserPreferencesService.getDateFormat('midDate', 'hourFormat'),
          );
        } else {
          order.eventItems[i].startDate = 'Date N/A';
          order.eventItems[i].startHour = 'Time N/A';
        }

        order.eventItems[i].items.sort(ctrl.sortOrderEventItems);
      }

      return order;
    },

    processThemeEmailVariables: function (themeData) {
      let themes = {};
      if (themeData.emailVariables) {
        themes = {
          printSummaryIntroMsg: $sce.trustAsHtml(themeData.emailVariables['orders-print-summary-intro-message']) || '',
          printSummaryClosingMsg:
            $sce.trustAsHtml(themeData.emailVariables['orders-print-summary-closing-message']) || '',
          printSummaryPaymentLink: $sce.trustAsHtml(
            themeData.textVariables['orders-print-summary-pay-online-message'] ||
              `Pay Online Now <span class="fa fa-caret-right" role="presentation"></span>`,
          ),
          printInvoiceHeaderMsg:
            $sce.trustAsHtml(themeData.emailVariables['orders-print-invoice-header-message']) || '',
          printInvoiceIntroMsg: $sce.trustAsHtml(themeData.emailVariables['orders-print-invoice-intro-message']) || '',
          printInvoiceClosingMsg:
            $sce.trustAsHtml(themeData.emailVariables['orders-print-invoice-closing-message']) || '',
          printInvoicePaymentLink: $sce.trustAsHtml(
            themeData.textVariables['orders-print-invoice-pay-online-message'] ||
              `Pay Online Now <span class="fa fa-caret-right" role="presentation"></span>`,
          ),
        };
      }
      return themes;
    },

    checkForExtRefId: function (order) {
      let event: TODO;
      let res = false;

      for (let i = 0, ii = order.eventItems.length; i < ii; i++) {
        if (!res) {
          event = order.eventItems[i];
          for (let j = 0, jj = event.items.length; j < jj; j++) {
            if (event.externalReferenceId) {
              res = true;
              break;
            }
          }
        } else {
          break;
        }
      }

      return res;
    },

    isDiscounted: function (ticket) {
      const o = currencyFloatToInteger(ticket.subtotal);
      const d = currencyFloatToInteger(ticket.discountedSubtotal);

      return o !== d;
    },

    isEventItemType: function (type) {
      console.log('OrdersPrintController::isEventItemType', type);
      return type === 'Event' || type === 'Combo';
    },

    isInventoryItemType: function (type) {
      return type === 'Inventory' || type === 'ComboInventory';
    },

    isMembershipItemType: function (type) {
      return type.match(/^Membership/);
    },

    hideFreeItem: function (ticket) {
      return (
        ctrl.hideItems &&
        ((ctrl.isDiscounted(ticket) && ticket.discountedSubtotal === '0.00') ||
          (!ctrl.isDiscounted(ticket) && ticket.subtotal === '0.00'))
      );
    },

    calculateBaseSubtotalPrice: function () {
      let baseSubtotal = 0;
      for (const eventItem of ensureArray(ctrl.model.eventItems)) {
        for (const ticket of eventItem.items) {
          let price = 0;
          if (ctrl.isDiscounted(ticket)) {
            price = currencyFloatToInteger(ctrl.getDiscountedSubtotal(ticket));
          } else {
            price = currencyFloatToInteger(ctrl.getSubtotal(ticket));
          }
          baseSubtotal += price;
        }
      }

      return integerToCurrencyFloat(baseSubtotal);
    },

    calculateTotalFees: function () {
      let total = 0;
      for (const fee of ctrl.feeItems) {
        if (ctrl.isDiscounted(fee)) {
          total += currencyFloatToInteger(fee.discountedSubtotal);
        } else {
          total += currencyFloatToInteger(fee.subtotal);
        }
      }
      return integerToCurrencyFloat(total);
    },

    calculateSalesTax: function () {
      let tax = 0;
      for (const eventItem of ensureArray(ctrl.model.eventItems)) {
        for (const ticket of eventItem.items) {
          let price = 0;
          if (ctrl.isDiscounted(ticket)) {
            price =
              currencyFloatToInteger(ticket.discountedSubtotal) -
              currencyFloatToInteger(ctrl.getDiscountedSubtotal(ticket));
          } else {
            price = currencyFloatToInteger(ticket.subtotal) - currencyFloatToInteger(ctrl.getSubtotal(ticket));
          }
          tax += price;
        }
      }

      return integerToCurrencyFloat(tax);
    },

    calculateTotalPrice: function () {
      let price = 0;
      let total = 0;
      let eventItem: TODO;
      let ticket: TODO;
      for (let i = 0, ii = ctrl.model.eventItems.length; i < ii; i++) {
        eventItem = ctrl.model.eventItems[i];
        for (let j = 0, jj = eventItem.items.length; j < jj; j++) {
          ticket = eventItem.items[j];
          if (ctrl.isDiscounted(ticket)) {
            price = currencyFloatToInteger(ticket.discountedSubtotal);
          } else {
            price = currencyFloatToInteger(ticket.subtotal);
          }
          total = total + price;
        }
      }
      return integerToCurrencyFloat(total);
    },

    printPage: function () {
      const printArea: TODO = $document.find('.orders-print-display');
      const oEventTable: TODO = printArea.find('.orders-print-body-events');
      const areaClone: TODO = printArea.clone();
      const summary: TODO = areaClone.find('.orders-print-summary');
      const eventTable: TODO = areaClone.find('.orders-print-body-events');
      let styles = '<style type="text/css">html, body { width: 100%; }</style>';

      areaClone.addClass('printable');
      areaClone.css({
        height: 'auto',
        width: '100%',
      });

      areaClone.find('.orders-print-heading').clone();

      if (ctrl.resizable && ctrl.tableRatio < 1) {
        //Do MAGIC
        styles =
          '<style type="text/css">html, body { width: ' +
          oEventTable.width() +
          'px;  @media print { @page { margin: 0; } } }</style>';
      }

      eventTable.css({
        width: '100%',
        margin: '0 auto',
        'transform-origin': '',
        transform: '',
      });

      console.log(
        'OrdersPrintController::resizeTable',
        oEventTable[0].getBoundingClientRect().width,
        oEventTable.width(),
      );
      areaClone.print({
        append: styles,
        renderTimeout: 500,
      });
    },

    getItemsQuantity: function (eventItems) {
      ctrl.totalMemberships = 0;
      ctrl.totalAddons = 0;
      ctrl.totalTicketQuantity = 0;

      for (let i = 0, ii = eventItems.length; i < ii; i++) {
        if (eventItems[i].items instanceof Array) {
          for (let j = 0, jj = eventItems[i].items.length; j < jj; j++) {
            if (eventItems[i].items[j] && !ctrl.hideFreeItem(eventItems[i].items[j])) {
              switch (eventItems[i].items[j].itemType.toLowerCase()) {
                case 'event':
                  ctrl.totalTicketQuantity += eventItems[i].items[j].quantity;
                  break;
                case 'inventory':
                  ctrl.totalAddons += eventItems[i].items[j].quantity;
                  break;
                default:
                  ctrl.totalMemberships += eventItems[i].items[j].quantity;
                  break;
              }
            }
          }
        }
      }
    },

    getPaid: function (eventItems) {
      let r = 0;
      const balance = ctrl.getOrderBalance();

      for (let i = 0, ii = eventItems.length; i < ii; i++) {
        if (eventItems[i].items instanceof Array) {
          for (let j = 0, jj = eventItems[i].items.length; j < jj; j++) {
            r += currencyFloatToInteger(eventItems[i].items[j].discountedSubtotal || 0);
          }
        }
      }

      r = r - balance;

      ctrl.totalPaid = '-' + integerToCurrencyFloat(r);
    },

    hasOrderBalance: function () {
      return ctrl.model.orderBalance && Number(ctrl.model.orderBalance);
    },

    generateOnlinePaymentLink: function (onlinePaymentUUID: string): string {
      return ordersDetailService.generateOnlinePaymentLink({
        brandedUrl: ThemesHelperService.getCompany('brandedUrl') || '',
        tenantId: ThemesHelperService.getCompany('tenantId'),
        consumerPage: ctrl.tenant.consumerPage,
        onlinePaymentUUID,
      });
    },

    fetchOnlinePaymentLink: function () {
      ordersDetailService.fetchOnlinePaymentLink(ctrl.model);
    },

    getDiscountedUnitPrice: function (ticket: TODO): string {
      if (ctrl.enableSalesTax && ticket.discountedBasePrice) {
        return ticket.discountedBasePrice;
      }
      return ticket.discountedUnitPrice;
    },

    getUnitPrice: function (ticket: { basePrice: string | null; unitPrice: TODO }): string {
      if (ctrl.enableSalesTax && ticket.basePrice) {
        return ticket.basePrice;
      }
      return ticket.unitPrice;
    },

    getDiscountedSubtotal: function (ticket: {
      discountedSubtotal: TODO;
      discountedBasePrice: TODO;
      quantity: number;
    }): string | number {
      if (ctrl.enableSalesTax && ticket.discountedBasePrice) {
        if (!ctrl.includesAtLeastOneItemWithBasePrice) {
          ctrl.includesAtLeastOneItemWithBasePrice = true;
        }

        const baseDiscountedSubtotal = currencyFloatToInteger(ticket.discountedBasePrice) * ticket.quantity;
        return integerToCurrencyFloat(baseDiscountedSubtotal);
      }

      return ticket.discountedSubtotal;
    },

    getSubtotal: function (ticket: { basePrice: string | null; quantity: number; subtotal: TODO }): string | number {
      if (ctrl.enableSalesTax && ticket.basePrice) {
        if (!ctrl.includesAtLeastOneItemWithBasePrice) {
          ctrl.includesAtLeastOneItemWithBasePrice = true;
        }

        const baseSubtotal = currencyFloatToInteger(ticket.basePrice) * ticket.quantity;
        return integerToCurrencyFloat(baseSubtotal);
      }

      return ticket.subtotal;
    },

    getTotalTicketQuantity: function () {
      if (ctrl.enableSalesTax) {
        return ctrl.baseTotalTicketQuantity;
      }
      return ctrl.totalTicketQuantity;
    },

    getTotalAddons: function () {
      if (ctrl.enableSalesTax) {
        return ctrl.baseTotalAddons;
      }
      return ctrl.totalAddons;
    },

    getTotalMemberships: function () {
      if (ctrl.enableSalesTax) {
        return ctrl.baseTotalMemberships;
      }
      return ctrl.totalMemberships;
    },

    success: {
      init: function (response) {
        console.log(response);
      },

      getOrderEventDetails: function (response) {
        //process the order after getting the order event details
        ctrl.processOrderEvents(response.data.payload);
        ctrl.getOrder(ctrl.order.orderId);
      },

      getThemes: function (response) {
        ctrl.themes = ctrl.processThemeEmailVariables(response.data.payload);
      },

      getPartner: function (response) {
        ctrl.accountCompany = response.data.payload.company;
        ctrl.primaryContact = response.data.payload.primaryContact;
      },

      getAccountContacts: function (response) {
        // TODO: function doesnt exist in the controller,
        // also looks like its dead code
        // ctrl.accountContact = ctrl.findContact(response.data.payload.list, ctrl.model.accountContactId);
      },

      getTransactions: function (response) {
        ctrl.transactions = response.data.payload
          .filter(function (item) {
            return !/Sale/i.test(item.transaction.transactionType);
          })
          .reduce(function (transactions, item) {
            if (
              transactions.filter(function (transaction) {
                return transaction.id === item.transaction.id;
              }).length === 0
            ) {
              transactions.push(item.transaction);
            }

            return transactions;
          }, [])
          .map(function (transaction) {
            let method = '';
            let creditCard = 'Credit Card';
            let lastFour = '';
            let r: TODO = null;

            switch (transaction.paymentMethod.toUpperCase()) {
              case 'VOUCHER':
                method = 'Voucher #' + transaction.paymentId;
                break;

              case 'CHECK':
                method = 'Check #' + transaction.paymentId;
                break;

              case 'CASH':
                method = 'Cash';
                break;

              case 'CREDIT_CARD':
                creditCard = transaction.creditCardBrand || creditCard;
                lastFour = transaction.ccLastFourDigits || lastFour;
                method = creditCard.toUpperCase() + '  x' + lastFour;
                break;

              case 'STORED_VALUE':
                lastFour = transaction.lastFour || '';
                method = 'Gift Card';
                break;

              default:
                method = transaction.paymentMethod + ' ' + transaction.paymentId;
            }

            if (
              transaction.paymentId ||
              transaction.paymentMethod.toUpperCase() === 'CASH' ||
              transaction.paymentMethod.toUpperCase() === 'CREDIT_CARD' ||
              transaction.paymentMethod.toUpperCase() === 'VOUCHER' ||
              transaction.paymentMethod.toUpperCase() === 'CHECK' ||
              transaction.paymentMethod.toUpperCase() === 'STOREDVALUE'
            ) {
              r = {
                date: $filter('date')(
                  CalendarService.parseDate(transaction.transactionDate),
                  UserPreferencesService.getDateFormat('midDate', 'dateFormat'),
                ),
                method: method,
                refund: /Refund/i.test(transaction.transactionType) ? '(Refund)' : '',
                amount: transaction.transactionAmount,
              };
            }

            return r;
          })
          .filter(function (item) {
            return !!item;
          });
      },

      getOrder: function (response) {
        // Check if account order or normal order for permissions
        if (response.data.payload.accountId) {
          ctrl.isAccount = true;
          ctrl.getPartner(response.data.payload.accountId);
          if (response.data.payload.accountContactId) {
            ctrl.getAccountContacts(response.data.payload.accountId);
          }
        }
        ctrl.model = ctrl.process(response.data.payload);
        ctrl.isTaxExempt = !!response.data.payload.isTaxExempt;
        for (const k in ctrl.model) {
          if (ctrl.model.hasOwnProperty(k)) {
            ctrl.hasData = true;
            break;
          }
        }
        ctrl.hasExtRefId = ctrl.checkForExtRefId(response.data.payload);
        ctrl.getItemsQuantity(response.data.payload.eventItems);
        ctrl.getPaid(response.data.payload.eventItems);
        if (ctrl.model.paymentMethodName === 'Check') {
          ctrl.checkNumber = ctrl.model.paymentId;
        } else {
          ctrl.voucherNumber = ctrl.model.paymentId;
        }
        ctrl.isLoaded = true;
        ctrl.printReady = true;

        ctrl.fetchOnlinePaymentLink();
      },
    },

    error: {
      init: function (response) {
        console.log(response);
      },
      getOrderEventDetails: function (response) {
        ctrl.isLoaded = true;
      },
      getTransactions: function (response) {
        console.error('Transactions not loaded');
      },
      getThemes: function (response) {
        ctrl.themes = {};
      },
      getAccountContacts: function (response) {
        console.log(response);
      },
      getPartner: function (response) {
        console.log(response);
      },
      getOrder: function (response) {
        ctrl.isLoaded = true;
      },
    },

    onHideItemsUpdate: function (e, data) {
      ctrl.hideItems = data;
      if (ctrl.model.eventItems) {
        ctrl.getItemsQuantity(ctrl.model.eventItems);
      }
    },

    onDateFormatLoad: function () {
      ctrl.currentDate = $filter('date')(
        CalendarService.parseDate(new Date()),
        UserPreferencesService.getDateFormat('midDate', 'dateFormat'),
      );
    },
  };

  $scope.ordersPrint = ctrl;
  ctrl.init();
}

export const OrdersPrintControllerModule = angular
  .module('acme.view.application.orders.print.controllers', [
    'acme.common.service.api',
    'acme.common.service.helpers',
    'acme.common.service.validate',
    'acme.common.service.calendar',
    'acme.common.service.order',
    'acme.common.directive.renderFinish',
    'acme.common.service.userPreferences',
    'acme.common.service.auth',
    'acme.common.service.themesHelper',
    OrdersDetailServiceModule,
  ])

  .controller('OrdersPrintController', OrdersPrintController).name;
